import { apiV2DataMonitorAlarmRuleInfoPost } from '@maxtropy/device-customer-apis-v2';
import {
  BaseInfoContent,
  Button,
  Form,
  FormTitle,
  SubContent,
  Tag,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { parseOnDisplay } from '@/shared/components/FormulaInput';
import { AlarmLevel, AlarmLevelColorDisplay, AlarmLevelDisplay } from '@/shared/types';

import { DataMonitoringRuleType, DataMonitoringRuleTypeDisplay } from './constants';
import MonitoringDeviceModal from './MonitoringDeviceModal';

const formatValueWithUnit = (value: number | string | undefined, unit: string) =>
  value === undefined || value === null ? '--' : `${value} ${unit}`;

const DataMonitoringAlarmRuleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [monitoringDeviceOpen, setMonitoringDeviceOpen] = useState(false);
  const ruleId = Number(id);
  const hasValidRuleId = Number.isFinite(ruleId);

  const { data: info, loading: infoLoading } = useRequest(() => apiV2DataMonitorAlarmRuleInfoPost({ id: ruleId }), {
    ready: hasValidRuleId,
    refreshDeps: [ruleId],
  });

  const dataPropertyMap = useMemo(
    () =>
      new Map(
        (info?.relatedProperties ?? []).flatMap(property =>
          property.id === undefined || !property.name ? [] : [[String(property.id), property.name] as const]
        )
      ),
    [info?.relatedProperties]
  );

  const monitoringPropertyNames =
    info?.monitorDataProperties
      ?.map(property => property.name || (property.id === undefined ? '' : String(property.id)))
      .filter(Boolean)
      .join('、') || '--';

  return (
    <Wrapper
      routes={[...(breadcrumbRoutes?.routes ?? []), { name: '数据监测报警规则详情' }]}
      className="page_wrapper"
      isLoading={infoLoading}
    >
      <FormTitle title="数据监测报警规则详情" />
      <SubContent>
        <BaseInfoContent layout="vertical">
          <Form.Item label="规则名称">{info?.ruleName ?? '--'}</Form.Item>
          <Form.Item label="报警等级">
            {info?.alarmLevel === undefined ? (
              '--'
            ) : (
              <Tag border="solid" color={AlarmLevelColorDisplay[info.alarmLevel as AlarmLevel]}>
                {AlarmLevelDisplay[info.alarmLevel as AlarmLevel]}
              </Tag>
            )}
          </Form.Item>
          <Form.Item label="报警类型">
            {info?.ruleType === undefined
              ? '--'
              : DataMonitoringRuleTypeDisplay[info.ruleType as DataMonitoringRuleType] ?? '--'}
          </Form.Item>
          <Form.Item label="适用设备类目">{info?.deviceTypeName ?? '--'}</Form.Item>
          <Form.Item label="物模型型号">{info?.physicalModelName ?? '--'}</Form.Item>
          <Form.Item label="选择设备">
            <Space size={8}>
              <Button type="link" disabled={!hasValidRuleId} onClick={() => setMonitoringDeviceOpen(true)}>
                查看
              </Button>
              <span>
                已选择 <span style={{ color: 'var(--mx-error-color)' }}>{info?.deviceIds?.length ?? 0}</span> 个
              </span>
            </Space>
          </Form.Item>

          {info?.ruleType === DataMonitoringRuleType.COMMUNICATION_STATUS && (
            <>
              <Form.Item label="监测数据属性">{monitoringPropertyNames}</Form.Item>
              <Form.Item label="监测时间范围">每 {formatValueWithUnit(info.monitorTimeRange, 'min')}</Form.Item>
              <Form.Item label="上传数据个数">
                {info.uploadDataCount === undefined ? '--' : `>= ${info.uploadDataCount}`}
              </Form.Item>
            </>
          )}

          {info?.ruleType === DataMonitoringRuleType.DATA_EXCEPTION && (
            <>
              <Form.Item label="报警公式">
                <div style={{ wordBreak: 'break-all' }}>
                  {info.alarmFormula
                    ? parseOnDisplay(info.alarmFormula, dataPropertyMap, dataPropertyMap) || '--'
                    : '--'}
                </div>
              </Form.Item>
              <Form.Item label="报警持续时间">{formatValueWithUnit(info.alarmDuration, 's')}</Form.Item>
            </>
          )}
        </BaseInfoContent>
      </SubContent>
      <Space className="sticky-footer">
        <Button onClick={() => navigate('/device/rule/data-monitoring')}>返回</Button>
      </Space>
      <MonitoringDeviceModal
        readOnly
        open={monitoringDeviceOpen}
        ruleId={hasValidRuleId ? ruleId : undefined}
        readOnlySelectedDeviceIds={info?.deviceIds}
        onClose={() => setMonitoringDeviceOpen(false)}
      />
    </Wrapper>
  );
};

export default DataMonitoringAlarmRuleDetail;
